import { useEffect, useMemo, useRef } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import type { GoogleDriveSourceSettingsViewModel } from "./GoogleDriveSourceSettings.ViewModel.types";
import type {
  GoogleDriveConfiguration,
  GoogleDriveSettingKey,
} from "./GoogleDriveSourceSettings.types";

/** Missing means on, as on the server. */
const isOn = (configuration: GoogleDriveConfiguration, key: GoogleDriveSettingKey): boolean =>
  configuration[key] ?? true;

/**
 * Google Drive's settings: which kinds of task to bring in, and where to look for
 * assigned comments. Nothing is fetched; Drive needs no lists to choose from.
 */
export const useGoogleDriveSourceSettingsViewModel = (
  model: SourceSettingsModel,
): GoogleDriveSourceSettingsViewModel => {
  const onUpdateRef = useRef(model.onUpdate);
  const editingConnectionRef = useRef(model.editingConnection);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.GoogleDrive && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<GoogleDriveConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  // Only ever called from a handler, never during render.
  const save = (key: GoogleDriveSettingKey, checked: boolean): void => {
    updateConnectionConfiguration<GoogleDriveConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({ ...cfg, [key]: checked }),
    );
  };

  const isStarredFilesOn = isOn(configuration, "includeStarredFiles");
  const isAssignedCommentsOn = isOn(configuration, "includeAssignedComments");

  return {
    isVisible,
    isStarredFilesOn,
    isAssignedCommentsOn,
    isSharedWithMeOn: isOn(configuration, "includeSharedWithMe"),
    isSharedDrivesOn: isOn(configuration, "includeSharedDrives"),
    isCoverageEditable: isAssignedCommentsOn,
    hasNothingSelected: !isStarredFilesOn && !isAssignedCommentsOn,
    onToggleStarredFiles: (checked: boolean) => save("includeStarredFiles", checked),
    onToggleAssignedComments: (checked: boolean) => save("includeAssignedComments", checked),
    onToggleSharedWithMe: (checked: boolean) => save("includeSharedWithMe", checked),
    onToggleSharedDrives: (checked: boolean) => save("includeSharedDrives", checked),
  };
};
